import '@/assets/tailwind.css'
import '@hantecmarket/hantec-ui-widget/style.css'
import 'maz-ui/styles'
import '@/assets/main.css'
import '@/core/Extenssions'
import { useLanguageSwitcher } from '@hantecmarket/widget-core'
import moengage from '@moengage/web-sdk'
import * as Sentry from '@sentry/vue'
import { createHead } from '@vueuse/head'
import { createPinia } from 'pinia'
import { createApp, nextTick } from 'vue'

import { preloadSeoSlugs } from '@/utils/cmsRoutesMap/cmsRoutesMap'

import App from './App.vue'
import router from './router'

import 'country-flag-emoji-polyfill'
import './style.scss'

const head = createHead()

const TRACKING_STORAGE_PREFIX = '_hmtracking_'

const clearLocalStorageExceptTracking = () => {
  try {
    const keys = Object.keys(localStorage)
    for (const key of keys) {
      if (!key.startsWith(TRACKING_STORAGE_PREFIX)) {
        localStorage.removeItem(key)
      }
    }
  } catch (error) {
    console.error('Failed to clean localStorage:', error)
  }
}

if (!AbortSignal.timeout) {
  AbortSignal.timeout = function (ms) {
    const controller = new AbortController()
    setTimeout(() => controller.abort(), ms)
    return controller.signal
  }
}

// Extend the global AbortSignal interface to declare `any` method
declare global {
  interface AbortSignalConstructor {
    any(signals: AbortSignal[]): AbortSignal
  }
}

// Polyfill for `AbortSignal.any`
if (!('any' in AbortSignal)) {
  Object.defineProperty(AbortSignal, 'any', {
    value: function (signals: AbortSignal[]): AbortSignal {
      const controller = new AbortController()

      for (const signal of signals) {
        if (signal.aborted) {
          controller.abort()
          return controller.signal
        }
        signal.addEventListener('abort', () => controller.abort(), { once: true })
      }

      return controller.signal
    },
    writable: true,
    configurable: true
  })
}

export {}

async function initializeApp() {
  try {
    await preloadSeoSlugs()
  } catch (error) {
    console.error('Failed to preload SEO slugs:', error)
  }

  const languageSwitcher = useLanguageSwitcher()

  const pathLangMatch = window.location.pathname.match(/^\/(en|pt|es|th|ko|zh-hans|zh-hant|jp)/)
  const langFromPath = pathLangMatch ? pathLangMatch[1] : null

  const lang = langFromPath || 'en'

  // TODO  just clear lang
  clearLocalStorageExceptTracking()
  languageSwitcher.setLang(lang)

  const app = createApp(App)

  const moengageRoutes = ['demo-registration', 'live-registration']
  router.beforeEach((to, from, next) => {
    if (moengageRoutes.includes(to.name as string)) {
      try {
        // Moengage
        const { VITE_ENV, VITE_MOE_KEY } = import.meta.env
        const isProd =
          VITE_ENV === 'prod' ||
          VITE_ENV === 'prod-pro' ||
          VITE_ENV === 'prod-mu' ||
          VITE_ENV === 'prod-hk' ||
          VITE_ENV === 'prod-cn'

        moengage.initialize({
          app_id: VITE_MOE_KEY,
          cluster: 'dc_2',
          debug_logs: isProd ? 0 : 1,
          disable_web_push: isProd
        })
        // Moengage \ end
      } catch (error) {
        console.log('moe', error)
        throw new Error('MOE')
      }
    }
    next()
  })

  // Add global error handler
  app.config.errorHandler = (err: any, vm, info: string) => {
    Sentry.init({
      app,
      dsn: 'https://0ce85e58e347515b33aefa76d0ac379e@o4508794660782080.ingest.de.sentry.io/4508795401404496',
      integrations: []
    })

    console.error('Global error caught:', err)

    // Check for known MoEngage errors to ignore them
    const isMoEngageError =
      err.message?.includes('Service worker register failed') ||
      err.message?.includes('unsupported MIME type') ||
      err.message?.includes('registering serviceworker')

    if (isMoEngageError) {
      console.log('MoEngage-related error bypassed:', err.message)
      return
    }

    // Handle 404 errors
    if (err.message === '404') {
      console.log(err.message)
      router.push({ name: 'not-found' })
    } else if (err.message !== 'MOE') {
      console.error('Unexpected error:', err, info)
      router.push({ name: 'not-found' })
    }
  }
  app.use(router)
  app.use(head)
  app.use(createPinia())

  app.mount('#app')

  nextTick(() => {
    if (typeof window === 'undefined' || !window) {
      return
    }

    // place where we load Live Chat Widget
    import('./utils/gtmLoader')
      .then(({ loadGtmScript, setupGtmLazyLoading }) => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(loadGtmScript, { timeout: 2000 })
        } else {
          setTimeout(loadGtmScript, 0)
        }

        setupGtmLazyLoading()
      })
      .catch((error) => {
        console.error('Failed to load GTM loader module:', error)
      })
  })
}

initializeApp()
